import { useState } from 'react';

type Status = 'idle' | 'success' | 'error';

const SubscribeForm = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const guard = String(payload.get('contact_alt') ?? '').trim();
    if (guard) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    payload.delete('contact_alt');

    setStatus('idle');
    setFormError('');

    try {
      const body = new URLSearchParams(payload as unknown as [string, string][]).toString();
      const response = await fetch('https://readdy.ai/api/form/dahg5kroh653ivfvnu20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(String(serverMsg) || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试。');
    }
  };

  return (
    <form
      id="subscribe-form"
      data-readdy-form
      action="https://readdy.ai/api/form/dahg5kroh653ivfvnu20"
      method="post"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-300/70 bg-background-50 p-5"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">订阅追更提醒</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
        留下邮箱，事件出现关键反转、辟谣或当事人回应时，我们会发一封简短提醒。
      </p>

      <div className="mt-4 space-y-3">
        <div>
          <label htmlFor="sub-name" className="block text-xs font-medium text-foreground-700">称呼</label>
          <input
            id="sub-name"
            name="name"
            type="text"
            autoComplete="name"
            placeholder="怎么称呼你"
            className="mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label htmlFor="sub-email" className="block text-xs font-medium text-foreground-700">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="sub-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            className="mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label htmlFor="sub-topic" className="block text-xs font-medium text-foreground-700">最关注的方向</label>
          <select
            id="sub-topic"
            name="topic"
            defaultValue="事件反转"
            className="mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          >
            <option value="事件反转">事件反转</option>
            <option value="辟谣澄清">辟谣澄清</option>
            <option value="当事人回应">当事人回应</option>
            <option value="直播带货">直播带货</option>
            <option value="内容版权">内容版权</option>
          </select>
        </div>

        <div>
          <label htmlFor="sub-lead" className="block text-xs font-medium text-foreground-700">想追踪的线索（可选）</label>
          <textarea
            id="sub-lead"
            name="lead"
            rows={3}
            maxLength={500}
            placeholder="简单描述事件，并尽量附上可公开查证的链接"
            className="mt-1 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div className="field-alt-guard" aria-hidden="true">
          <label htmlFor="sub-contact-alt">请勿填写</label>
          <input
            id="sub-contact-alt"
            name="contact_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>
      </div>

      <button
        type="submit"
        className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
      >
        <i className="ri-notification-3-line" aria-hidden="true" />
        订阅追更提醒
      </button>

      {status === 'success' && (
        <p className="mt-3 flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-800">
          <i className="ri-checkbox-circle-line" aria-hidden="true" />
          订阅成功，感谢你的关注。
        </p>
      )}
      {status === 'error' && (
        <p className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-accent-800">
          {formError}
        </p>
      )}
    </form>
  );
};

export default SubscribeForm;